import { C537 } from '@/core/translations/corporations/C537';
import { CCYZ } from '@/core/translations/corporations/CCYZ';
import { CHAH } from '@/core/translations/corporations/CHAH';
import { CHWZ } from '@/core/translations/corporations/CHWZ';
import { CNCK } from '@/core/translations/corporations/CNCK';
import { CRSV } from '@/core/translations/corporations/CRSV';
import { CRUK } from '@/core/translations/corporations/CRUK';
import { CZJS } from '@/core/translations/corporations/CZJS';
import { CRFW } from '@/core/translations/corporations/CRFW';
import { CTNY } from '@/core/translations/corporations/CTNY';
import { CTRC } from '@/core/translations/corporations/CTRC';
import { CRUT } from '@/core/translations/corporations/CRUT';
import { CRUA } from '@/core/translations/corporations/CRUA';

type CorpTranslationProperty = Record<string, string | Record<string, string>>;
export interface CorpTranslation {
    book_unit?: Record<string, string>;
    contact_card?: Record<string, CorpTranslationProperty>;
    cta?: CorpTranslationProperty;
    index?: Record<string, string>;
    rapidstor?: CorpTranslationProperty;
    sizeSlider?: CorpTranslationProperty;
    unit_listing?: Record<string, string>;
    wizard?: {
        back?: string;
        reservation?: string;
        scheduleMoveIn?: string;
        copy?: string;
        selectDate?: string;
        next?: string;
        confirm_booking?: string;
        payViaFinverse?: string;
        termsAndConditionsAgree?: string;
        marketingAgree?: string;
        assistanceMovingAgree?: string;
        dateOfBirth?: string;
        otherError?: string;
        autobill?: string;
        autobillDetails?: {
            title?: string;
            directDebit?: string;
            creditCard?: string;
            bsbNumber?: string;
            accountNumber?: string;
            none?: string;
        };
        personal?: string;
        business?: string;
        contactDetails?: {
            title?: string;
            titles?: {
                mr?: string;
                mrs?: string;
                miss?: string;
                ms?: string;
                dr?: string;
            };
            commercial?: string;
            company?: string;
            companyNumber?: string;
        };
        alternateContactDetails?: {
            title?: string;
            subtitle?: string;
            sameAddress?: string;
        };
        accountDetails?: {
            title?: string;
            copy?: string;
            password?: string;
            confirmPassword?: string;
        };
        reservationWithFee?: {
            orderNumber?: string;
            confirmationText?: string;
        };
        reservationConfirmed?: {
            title?: string;
            subtitle?: string;
            finaliseBooking?: string;
        };
        findReservation?: {
            title?: string;
            subtitle?: string;
            inputLabel?: string;
            placeholder?: string;
            submit?: string;
            notFoundError?: string;
        };
        insuranceCoverage?: {
            title?: string;
            subtitle?: string;
            coverage?: string;
            for_up_to?: string;
            noInsuranceSelected?: string;
            manualEntryLabel?: string;
            disclaimer?: string;
            flatRateInsurance?: string;
            selfInsurance?: string;
            usSupply?: string;
            subTitle?: string;
            proofDisclaimer?: string;
            fileTypePlease?: string;
        };
        addressProof?: {
            title?: string;
            subtitle?: string;
            fileUploadHelpText?: string;
            uploading?: string;
            success?: string;
            error?: string;
        };
        insuranceProof?: {
            title?: string;
            subtitle?: string;
            fileUploadHelpText?: string;
            uploading?: string;
            success?: string;
            error?: string;
        };
        boxShop?: {
            title?: string;
            outOfStock?: string;
            pickUpLocation?: string;
            showAll?: string;
            showLess?: string;
        };
        pricingTable?: {
            title?: string;
            charge?: string;
            amount?: string;
            discount?: string;
            tax?: string;
            total?: string;
        };
        identityVerification?: {
            title?: string;
            driverLicenseDetails?: string;
            uploadDriverLicense?: string;
            fileUploadHelpText?: string;
            idDocumentNumber?: string;
        };
        paymentForm?: {
            title?: string;
            csvAmexPlaceholder?: string;
            errors?: {
                cardHolderInvalid?: string;
                cardNumberInvalid?: string;
                cardNumberLuhnInvalid?: string;
                cardExpiryInvalid?: string;
                cardExpiryExpired?: string;
                cardCsvInvalid?: string;
            };
        };
        bookingConfirmation?: {
            title?: string;
            subtitle?: string;
            referenceCode?: string;
            customerSurvey?: {
                title?: string;
                subtitle?: string;
                submit?: string;
            };
            idVerifyButtonText?: string;
            idVerifyText?: string;
        };
        marketingSurvey?: {
            title?: string;
            subtitle?: string;
            Marketing?: string;
            MktgDistance?: string;
            MktgWhy?: string;
            MktgType?: string;
            MktgWhat?: string;
            MktgReason?: string;
            submit?: string;
            defaultOption?: string;
            questions?: string[];
        };
    };
}

/**
 * Corporation specific translations.
 *
 * @loadbearing
 */
export const CORPORATION_TRANSLATIONS = {
    C537,
    CRUK,
    CHAH,
    CCYZ,
    CZJS,
    CHWZ,
    CNCK,
    CRFW,
    CRSV,
    CTNY,
    CTRC,
    CRUT,
    CRUA,
};

export type CorporationCode = keyof typeof CORPORATION_TRANSLATIONS;

export const getCorpTranslations = (corpCode: CorporationCode) => CORPORATION_TRANSLATIONS[corpCode];

/**
 * Decode the snippet-level translations carried on the config as a single
 * JSON-encoded string into a per-language, dot-keyed map
 * ({ en: { 'cta.select': 'Reserve' } }). Resilient: returns {} for an absent or
 * malformed value so a bad snippet never breaks boot.
 */
export const parseSnippetTranslations = (
    raw: string | undefined | null,
): Record<string, Record<string, string>> => {
    if (!raw) return {};
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

/**
 * Turn the backend's flat, dot-keyed translation map into the nested object
 * shape i18next resources use (top-level key = namespace). Inverse of the
 * Automate import's flatten — e.g.
 *   { 'wizard.findReservation.title': 'x' }
 * becomes
 *   { wizard: { findReservation: { title: 'x' } } }.
 */
export const unflattenTranslations = (
    flat: Record<string, string>,
): Record<string, unknown> => {
    const nested: Record<string, unknown> = {};

    for (const [dotKey, value] of Object.entries(flat)) {
        const parts = dotKey.split('.');
        let cursor = nested;

        parts.forEach((part, index) => {
            if (index === parts.length - 1) {
                cursor[part] = value;
            } else {
                if (typeof cursor[part] !== 'object' || cursor[part] === null) {
                    cursor[part] = {};
                }
                cursor = cursor[part] as Record<string, unknown>;
            }
        });
    }

    return nested;
};
